import { Button, useToast } from '@chakra-ui/react';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import { ActionPopover } from '@gamma/ui/overlay';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useContext } from 'react';
import { i18n } from '../../../../../../shared/localization';
import { AuthContext } from '../../../../../../shared/providers/AuthProvider';
import { useListUsers } from '../../../../../admin/queries';
import {
  GET_DETECTIONS,
  QUERY_DETECTIONS_PAGINATED,
  useAssignDetections,
} from '../../../../queries';

import { IGetDetectionVariables } from '../../..';
import { DetectionQueryVariables } from '../../../../pages';
import { useDetectionContext } from '@investigator/features/detections/context';

const { controls, detection, toasts } = i18n.pages.detections;

const { assignTo, assignToMe, assignUser } = controls;
const { unassigned } = detection;

interface AssignUserPopoverProps {
  refetchMain?: boolean;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
}

export const AssignUserPopover = ({
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
}: AssignUserPopoverProps) => {
  const showToastMessage = useToast();

  const { user } = useContext(AuthContext);
  const { detectionData } = useDetectionContext();

  const { assignment_info, detection_id, update_info } = detectionData || {};

  const { loading: listUsersLoading, data: { listUsers = [] } = {} } =
    useListUsers({
      skip: !user?.attributes?.['custom:tenant_id'],
      variables: {
        tenants: [`${user?.attributes?.['custom:tenant_id']}`],
        groups: ['admin', 'analyst'],
      },
    });

  const refetchQueries = [
    {
      query: GET_DETECTIONS,
      variables: getDetectionVariables,
    },
  ];

  const [assignDetections, { loading: assignLoading, error: _assignError }] =
    useAssignDetections({
      awaitRefetchQueries: true,
      refetchQueries: !queryDetectionsPaginatedVariables
        ? refetchQueries
        : [
            ...refetchQueries,
            {
              query: QUERY_DETECTIONS_PAGINATED,
              variables: queryDetectionsPaginatedVariables,
            },
          ],
      onCompleted: () => {
        showToastMessage({
          status: 'success',
          title: toasts.success,
          description: toasts.assigneeSet,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: toasts.error,
          description: toasts.assigneeNotSet,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  return (
    <ActionPopover
      withArrow={false}
      title={assignUser}
      placement="bottom-start"
      isSearchable={true}
      data-testid="assign-detection-popover"
      value={assignment_info?.assigned_to_username ?? undefined}
      isLoading={assignLoading || listUsersLoading}
      options={
        listUsers?.length > 0
          ? [
              ...listUsers
                .filter((user) => !!user.alias)
                .filter((user) => user.status === 'active'),
              { alias: unassigned, username: '' },
            ]
              .map((investUser) => {
                return {
                  label:
                    user?.username === investUser?.username
                      ? assignToMe
                      : investUser?.alias,
                  value: investUser?.username,
                };
              })
              .sort((a, b) => a?.label.localeCompare(b?.label))
          : []
      }
      onApply={(selectedValue) => {
        assignDetections({
          variables: {
            items: [
              {
                assignment_info: {
                  assigned_to_username: selectedValue || null,
                },
                update_info: {
                  last_updated_timestamp: update_info?.last_updated_timestamp,
                },
                detection_id,
                tenant: user?.attributes?.['custom:tenant_id'],
              },
            ],
          },
        });
      }}
    >
      <Button
        variant="solid"
        colorScheme="gray"
        data-testid="assign-detection-trigger"
        leftIcon={
          assignLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <AccountCircleSharpIcon size="sm" />
          )
        }
      >
        {assignTo}
      </Button>
    </ActionPopover>
  );
};
